import { translateEntity, LANGUAGES } from '@uefadigital/panenka';

import { language } from '@utilities/browser';
import { loadFromAPIs } from '@utilities/fetch';
import { MemoryCache } from '@utilities/memory-cache';
import { getParameterByName } from '@utilities/url';
import { uefaApiBaseUrl } from '@utilities/WindowContext';

const showTag = getParameterByName('showTranslationTags') === 'true';

export const hydrateWithTranslations = <T>(obj: T, ln = language) => translateEntity(obj, LANGUAGES[ln?.toUpperCase()]);

type Vocabulary = {
  [lang: string]: Record<string, string>;
};

export type TranslationReplacement = string | number | boolean;
export type TranslationOptions = {
  abbr?: boolean;
  replace?: string | TranslationReplacement[] | Record<string, TranslationReplacement>;
  fallback?: string;
};

export const uefaVocabularyId = 125619;

const cache = new MemoryCache<number, Map<string, Vocabulary[string]>>();

export class TranslatorService {
  constructor() {
    this.loadVocabulary(uefaVocabularyId, language);
  }

  getSavedVocabulary(vocabularyId = uefaVocabularyId, lang: string) {
    return cache.get(vocabularyId)?.get(lang);
  }

  saveVocabulary(vocabularyId: number, lang: string, value: Vocabulary[string]) {
    if (!value) return;

    const cacheVocab = cache.get(vocabularyId) ?? new Map<string, Vocabulary[string]>();
    cacheVocab.set(lang, value);
    cache.set(vocabularyId, cacheVocab);
  }

  async loadVocabulary(vocabularyId = uefaVocabularyId, lang: string, signal?: AbortSignal) {
    const savedVocabulary = this.getSavedVocabulary(vocabularyId, lang);
    if (savedVocabulary) {
      return savedVocabulary;
    }

    const vocabulary = await loadFromAPIs<Vocabulary>(
      `/vocabulary/${vocabularyId}/${lang}`,
      {},
      { apiUrl: uefaApiBaseUrl, signal },
    );

    vocabulary && this.saveVocabulary(vocabularyId, lang, vocabulary[lang]);

    return this.getSavedVocabulary(vocabularyId, lang);
  }

  private replaceInWord(word: string, replace: TranslationOptions['replace']) {
    if (!replace || !word) return word;

    if (typeof replace === 'string') {
      replace = `${replace}`.split('|');
    }

    if (Array.isArray(replace)) {
      replace.forEach((rpl, i) => {
        word = word.replace(`{${i}}`, `${rpl}`);
      });
    }

    if (typeof replace === 'object') {
      Object.entries(replace).forEach(([key, value]) => {
        word = word.replace(`{${key}}`, `${value}`);
      });
    }

    return word;
  }

  getWordFromVocabulary(translations: Record<string, string>, tag: string, options?: TranslationOptions) {
    // if tag is empty return empty string
    if (!tag) return options?.fallback ?? '';

    const { abbr, replace, fallback } = options ?? {};

    const tagToUse = abbr ? `${tag}_abbr` : tag;

    // if translations are not loaded return tag
    if (!translations) return fallback ?? tagToUse;

    // get translation as key and fallback to case insensitive search
    let word =
      translations[tagToUse] ??
      Object.entries(translations).find(([key]) => key?.toLowerCase() === tagToUse?.toLowerCase())?.[1];

    // if translation is not found and abbr is true, try to find translation without abbr
    if (abbr && !word) {
      word =
        translations[tag] ??
        Object.entries(translations).find(([key]) => key?.toLowerCase() === tag?.toLowerCase())?.[1];
    }

    if (Array.isArray(word)) {
      word = word.join('\\n');
    }

    // apply replace, if translation is not found return tag
    const result = this.replaceInWord(word, replace) ?? fallback ?? tagToUse;

    return showTag ? `${result} [${tag}]` : result;
  }

  async getText(tag: string, options?: TranslationOptions) {
    if (!tag) return options?.fallback ?? '';

    const translations = await this.loadVocabulary(uefaVocabularyId, language);

    return this.getWordFromVocabulary(translations, tag, options);
  }

  static readonly instance = new TranslatorService();
}

const TranslatorServiceInstance = TranslatorService.instance;
export default TranslatorServiceInstance;
